import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { getSystemConfig, SystemConfig } from '@/services/configService';

interface ConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConfigDialog: React.FC<ConfigDialogProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common');
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
      getSystemConfig()
        .then(setConfig)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const sheetDisplayNames: Record<string, string> = {
    sim_owners: t('config.sheets.simOwners', 'SIM Owners'),
    modem_owners: t('config.sheets.modemOwners', 'Modem Owners'),
    lloyd_linden: t('config.sheets.lloydLinden', 'Lloyd Linden'),
    psi: t('config.sheets.psi', 'PSI'),
    expenditure: t('config.sheets.expenditure', 'Expenditure'),
    vendors: t('config.sheets.vendors', 'Vendors'),
    customers: t('config.sheets.customers', 'Customers'),
    part_numbers: t('config.sheets.partNumbers', 'Part Numbers'),
    employees: t('config.sheets.employees', 'Employees'),
    kanban: t('config.sheets.kanban', 'Kanban'),
    projects: t('config.sheets.projects', 'Projects'),
    blog: t('config.sheets.blog', 'Blog'),
  };

  const folderDisplayNames: Record<string, string> = {
    kanban_reviews: t('config.folders.kanbanReviews', 'Kanban Reviews'),
    pcb_gerber: t('config.folders.pcbGerber', 'PCB Gerber'),
    po_output: t('config.folders.poOutput', 'PO Output'),
    sq_output: t('config.folders.sqOutput', 'SQ Output'),
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-2xl sm:max-h-[85vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl z-50 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {t('config.title', 'System Configuration')}
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : error ? (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
                  {error}
                </div>
              ) : config ? (
                <>
                  {/* Google Sheets Section */}
                  <section>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/>
                        <path d="M7 7h2v2H7zm0 4h2v2H7zm0 4h2v2H7zm4-8h6v2h-6zm0 4h6v2h-6zm0 4h6v2h-6z"/>
                      </svg>
                      {t('config.googleSheets', 'Google Sheets')}
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(config.sheets).map(([key, sheet]) => (
                        <div
                          key={key}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {sheetDisplayNames[key] || key}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {sheet.id || t('config.notConfigured', 'Not configured')}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            {sheet.url && (
                              <a
                                href={sheet.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                title={t('config.openSheet', 'Open in Google Sheets')}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            )}
                            {sheet.id && (
                              <button
                                onClick={() => handleCopy(sheet.id, key)}
                                className="p-2 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                title={t('config.copyId', 'Copy ID')}
                              >
                                {copiedId === key ? (
                                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Google Drive Folders Section */}
                  <section>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-yellow-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.71 3.5L1.15 15l4.58 6.5h13.54l4.58-6.5L17.29 3.5H7.71zm6.57 0l5.57 9.5h-5.57V3.5zm-1.57 0v9.5H7.14L12.71 3.5zm-6.57.53L10.47 13H3.85l2.29-9.97zM3.58 14.5h7.14l-3.29 5h-7.14l3.29-5zm8.71 5l3.28-5h7.14l-3.28 5h-7.14zm3.56-5.5l4.33-8.97 2.24 8.97h-6.57z"/>
                      </svg>
                      {t('config.googleDrive', 'Google Drive Folders')}
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(config.drive_folders).map(([key, folderId]) => (
                        <div
                          key={key}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {folderDisplayNames[key] || key}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {folderId || t('config.notConfigured', 'Not configured')}
                            </div>
                          </div>
                          {folderId && (
                            <button
                              onClick={() => handleCopy(folderId, `folder_${key}`)}
                              className="p-2 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors ml-4"
                              title={t('config.copyId', 'Copy ID')}
                            >
                              {copiedId === `folder_${key}` ? (
                                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* System Info Section */}
                  <section>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                      </svg>
                      {t('config.systemInfo', 'System Info')}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-gray-700 dark:text-gray-300">Firestore Database</span>
                        <span className="text-gray-900 dark:text-white font-mono text-sm">
                          {config.firestore_db}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-gray-700 dark:text-gray-300">Redis</span>
                        <span className="text-gray-900 dark:text-white font-mono text-sm">
                          {config.redis_url || t('config.notConfigured', 'Not configured')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-gray-700 dark:text-gray-300">AI Parse</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          config.ai_parse_enabled
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                        }`}>
                          {config.ai_parse_enabled ? t('config.enabled', 'Enabled') : t('config.disabled', 'Disabled')}
                        </span>
                      </div>
                    </div>
                  </section>

                  {/* LINE Messaging Section */}
                  <section>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 5.82 2 10.5c0 4.12 3.53 7.57 8.29 8.35.32.07.76.21.87.48.1.25.07.63.03.88l-.14.84c-.04.25-.2.97.85.53s5.6-3.3 7.64-5.65C21.46 13.64 22 12.13 22 10.5 22 5.82 17.52 2 12 2zm-3.08 11.25H6.58a.55.55 0 01-.55-.55V8.42c0-.3.25-.55.55-.55.3 0 .55.25.55.55v3.73h1.79c.3 0 .55.25.55.55 0 .3-.25.55-.55.55zm1.77-.55c0 .3-.25.55-.55.55-.3 0-.55-.25-.55-.55V8.42c0-.3.25-.55.55-.55.3 0 .55.25.55.55v4.28zm4.43 0a.55.55 0 01-.44.54.56.56 0 01-.1.01.55.55 0 01-.45-.23l-2.46-3.35v3.03c0 .3-.25.55-.55.55-.3 0-.55-.25-.55-.55V8.42a.55.55 0 01.99-.33l2.46 3.35V8.42c0-.3.25-.55.55-.55.3 0 .55.25.55.55v4.28zm2.97-2.49c.3 0 .55.25.55.55 0 .3-.25.55-.55.55h-1.79v1.39h1.79c.3 0 .55.25.55.55 0 .3-.25.55-.55.55h-2.34a.55.55 0 01-.55-.55V8.42c0-.3.25-.55.55-.55h2.34c.3 0 .55.25.55.55 0 .3-.25.55-.55.55h-1.79v1.39h1.79z"/>
                      </svg>
                      {t('config.line.title', 'LINE Messaging')}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-gray-700 dark:text-gray-300">{t('config.line.status', 'Status')}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          config.line?.enabled
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                        }`}>
                          {config.line?.enabled ? t('config.enabled', 'Enabled') : t('config.disabled', 'Disabled')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-gray-700 dark:text-gray-300">{t('config.line.channelId', 'Channel ID')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900 dark:text-white font-mono text-sm">
                            {config.line?.channel_id || t('config.notConfigured', 'Not configured')}
                          </span>
                          {config.line?.channel_id && (
                            <button
                              onClick={() => handleCopy(config.line!.channel_id!, 'line_channel_id')}
                              className="p-1 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                              title={t('config.copyId', 'Copy ID')}
                            >
                              {copiedId === 'line_channel_id' ? (
                                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>
                </>
              ) : null}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('config.close', 'Close')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ConfigDialog;
